import { Fragment, useState, useReducer } from 'react';
import { Link } from 'react-router-dom';
import { resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import intl from 'react-intl-universal';

// @mui material components
import Card from '@mui/material/Card';
import { Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';
import MDLoadingButton from 'components/MDLoadingButton';

// Authentication layout components
import BasicLayout from 'views/Welcome/components/BasicLayout';

// Images
import bgImage from 'assets/images/background/bg-0.png';
import newBgImage from 'assets/images/bg-login-bai.jpeg';

import { validatePassword } from 'utils/misc';
import { RECOVERY_STATES } from 'shared/constants/passwordRecovery';

const initialState = {
  // page 1: enter username and request for verification code
  codeSent: false, // show page 1
  codeDeliveryFailed: false,
  codeDeliveryFailedReason: '',
  invalidEmail: false, // show message on page 1
  tooManyAttempts: false, // show message on page 1
  isSendingCode: false, // on click

  // page 2: enter code and reset password
  passwordIsReset: false, // show page 2 or 3
  emptyEntry: false, // show warning message;
  invalidResetForm: false,
  invalidResetFormMessage: '', // "inconsistentPassword" / "emptyCode" / "tooSimplePassword"
  isSettingPassword: false, // on click
  passwordResetFailed: false,
  passwordResetFailedReason: '',
};

const reducer = (state, action) => {
  switch (action.type) {
    case RECOVERY_STATES.SUBMIT_EMAIL: {
      return {
        ...state,
        isSendingCode: true,
      };
    }
    case RECOVERY_STATES.CODE_SENT: {
      return {
        ...state,
        codeSent: true,
        isSendingCode: false,
        codeDeliveryFailed: false,
        codeDeliveryFailedReason: '',
      };
    }
    case RECOVERY_STATES.CODE_DELIVERY_FAILED: {
      return {
        ...state,
        isSendingCode: false,
        codeSent: false,
        codeDeliveryFailed: true,
        codeDeliveryFailedReason: action.message,
      };
    }
    case RECOVERY_STATES.CLICK_PASSWORD_RESET_BUTTON: {
      return {
        ...state,
        isSettingPassword: true,
      };
    }
    case RECOVERY_STATES.INITIALIZE_RESET_FORM: {
      return {
        ...state,
        isSettingPassword: false,
        invalidResetForm: false,
        invalidResetFormMessage: '',
        passwordResetFailed: false,
        passwordResetFailedReason: '',
      };
    }
    case RECOVERY_STATES.INVALID_RESET_FORM: {
      return {
        ...state,
        invalidResetForm: true,
        invalidResetFormMessage: action.message,
        isSettingPassword: false,
      };
    }
    case RECOVERY_STATES.PASSWORD_RESET_SUCCESS: {
      return {
        ...state,
        isSettingPassword: false,
        passwordIsReset: true,
        invalidResetForm: false,
        passwordResetFailed: false,
        passwordResetFailedReason: '',
      };
    }
    case RECOVERY_STATES.PASSWORD_RESET_FAIL: {
      return {
        ...state,
        isSettingPassword: false,
        invalidResetForm: false,
        passwordIsReset: false,
        passwordResetFailed: true,
        passwordResetFailedReason: action.message,
      };
    }
    case RECOVERY_STATES.GO_BACK_TO_PAGE1: {
      return {
        ...state,
        codeSent: false,
        isSettingPassword: false,
        isSendingCode: false,
        invalidResetForm: false,
        passwordIsReset: false,
      };
    }
    default:
      return;
  }
};

export default function ResetPassword() {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [fields, setFields] = useState({
    code: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const loginMode = 0;

  const pallete = [
    { cardBackgroundColor: 'rgba(52, 52, 52, 0.8)' },
    { cardBackgroundColor: '#8B8B8B' },
  ];

  const handleInput = (event) => {
    const { name, value } = event.target;
    setFields({ ...fields, [name]: value });
  };

  const handleSendCodeClick = async (event) => {
    event.preventDefault();
    dispatch({ type: RECOVERY_STATES.SUBMIT_EMAIL });
    try {
      await resetPassword({ username: fields.email });
      dispatch({ type: RECOVERY_STATES.CODE_SENT });
    } catch (error) {
      dispatch({
        type: RECOVERY_STATES.CODE_DELIVERY_FAILED,
        message: error.name,
      });
    }
  };

  const handleResetPasswordClick = async (event) => {
    event.preventDefault();

    // check if the reset form entries are okay.
    dispatch({ type: RECOVERY_STATES.INITIALIZE_RESET_FORM });

    if (fields.code.length < 1) {
      dispatch({
        type: RECOVERY_STATES.INVALID_RESET_FORM,
        message: 'emptyCode',
      });
      return;
    } else if (!validatePassword(fields.password)) {
      dispatch({
        type: RECOVERY_STATES.INVALID_RESET_FORM,
        message: 'tooSimplePassword',
      });
      return;
    } else if (fields.password !== fields.confirmPassword) {
      dispatch({
        type: RECOVERY_STATES.INVALID_RESET_FORM,
        message: 'inconsistentPassword',
      });
      return;
    }

    dispatch({ type: RECOVERY_STATES.CLICK_PASSWORD_RESET_BUTTON });

    try {
      await confirmResetPassword({
        username: fields.email,
        confirmationCode: fields.code,
        newPassword: fields.password,
      });

      dispatch({ type: RECOVERY_STATES.PASSWORD_RESET_SUCCESS });
    } catch (error) {
      dispatch({
        type: RECOVERY_STATES.PASSWORD_RESET_FAIL,
        message: error.name,
      });
    }
  };

  const renderCodeDeliveryErrorMessage = (reason) => {
    switch (reason) {
      case 'LimitExceededException': {
        return (
          <Fragment>
            <Typography
              variant="body2"
              fontWeight="light"
              style={{ color: 'red' }}>
              {intl.get('password_recovery_retries_exceeded')}
            </Typography>
            <Typography
              variant="body2"
              fontWeight="light"
              style={{ color: 'red' }}>
              {intl.get('password_recovery_try_again_later')}
            </Typography>
          </Fragment>
        );
      }
      case 'UserNotFoundException': {
        return (
          <Typography
            variant="body2"
            fontWeight="light"
            style={{ color: 'red' }}>
            {intl.get('password_recovery_invalid_username')}
          </Typography>
        );
      }
      default:
        return;
    }
  };

  const renderInvalidResetFormErrorMessage = (reason) => {
    switch (reason) {
      case 'emptyCode': {
        return (
          <MDBox mb={1}>
            <MDTypography
              variant="body2"
              fontWeight="light"
              style={{ color: 'red' }}>
              {intl.get('password_recovery_empty_code')}
            </MDTypography>
          </MDBox>
        );
      }
      case 'inconsistentPassword': {
        return (
          <MDBox mb={1}>
            <MDTypography
              variant="body2"
              fontWeight="light"
              style={{ color: 'red' }}>
              {intl.get('password_recovery_inconsistent_password')}
            </MDTypography>
          </MDBox>
        );
      }
      case 'tooSimplePassword': {
        return (
          <MDBox mb={1}>
            <MDTypography
              variant="body2"
              fontWeight="light"
              style={{ color: 'red' }}>
              {intl.get('password_recovery_new_password_conditions')}
            </MDTypography>
            <MDTypography
              variant="body2"
              fontWeight="light"
              style={{ color: 'red' }}>
              {'\u2022'}{' '}
              {intl.get('password_recovery_new_password_conditions_length')}
            </MDTypography>
            <MDTypography
              variant="body2"
              fontWeight="light"
              style={{ color: 'red' }}>
              {'\u2022'}{' '}
              {intl.get('password_recovery_new_password_conditions_number')}
            </MDTypography>
            <MDTypography
              variant="body2"
              fontWeight="light"
              style={{ color: 'red' }}>
              {'\u2022'}{' '}
              {intl.get('password_recovery_new_password_conditions_uppercase')}
            </MDTypography>
            <MDTypography
              variant="body2"
              fontWeight="light"
              style={{ color: 'red', marginBottom: '10px' }}>
              {'\u2022'}{' '}
              {intl.get('password_recovery_new_password_conditions_lowercase')}
            </MDTypography>
          </MDBox>
        );
      }
      default:
        return;
    }
  };

  const renderPasswordResetFailedErrorMessage = (reason) => {
    switch (reason) {
      case 'CodeMismatchException': {
        return (
          <MDBox mb={1}>
            <MDTypography
              variant="body2"
              fontWeight="light"
              style={{ color: 'red' }}>
              {intl.get('password_recovery_invalid_code')}
            </MDTypography>
          </MDBox>
        );
      }
      case 'ExpiredCodeException': {
        // fall back to page 1
        return (
          <MDBox mb={1}>
            <MDTypography
              variant="body2"
              fontWeight="light"
              style={{ color: 'red' }}>
              {intl.get('password_recovery_expired_code')}
            </MDTypography>
          </MDBox>
        );
      }
      case 'LimitExceededException': {
        // fall back to page 1
        return (
          <Fragment>
            <Typography
              variant="body2"
              fontWeight="light"
              style={{ color: 'red' }}>
              {intl.get('password_recovery_retries_exceeded')}
            </Typography>
            <Typography
              variant="body2"
              fontWeight="light"
              style={{ color: 'red' }}>
              {intl.get('password_recovery_try_again_later')}
            </Typography>
          </Fragment>
        );
      }
      default:
        return;
    }
  };

  function renderRequestCodeForm() {
    return (
      <Card
        id="BasicLayoutChildrenCard"
        sx={{ backgroundColor: pallete[loginMode].cardBackgroundColor }}>
        <MDBox pt={4} pb={3} px={3} sx={{ width: '100%' }}>
          <MDBox mb={3}>
            <MDTypography style={{ color: 'white' }}>
              {intl.get('password_recovery_enter_username')}
            </MDTypography>
          </MDBox>
          <MDBox component="form" role="form" onSubmit={handleSendCodeClick}>
            <MDBox mb={2}>
              <MDInput
                required
                fullWidth
                name="email"
                value={fields.email}
                onChange={handleInput}
                sx={{ input: { background: '#FFFFFF', color: '#000' } }}
              />
            </MDBox>
            {state.codeDeliveryFailed &&
              renderCodeDeliveryErrorMessage(state.codeDeliveryFailedReason)}

            <MDBox mt={4} mb={1}>
              <MDLoadingButton
                loadingPosition="start"
                loading={state.isSendingCode}
                type="submit"
                variant="gradient"
                sx={{
                  backgroundColor: '#194796',
                  '&:hover': {
                    backgroundColor: '#194796',
                  },
                  '&:disabled': {
                    backgroundColor: '#194796',
                  },
                }}
                fullWidth>
                <MDTypography variant="h5" fontWeight="bold" color="white">
                  {state.isSendingCode
                    ? intl.get('login_please_wait')
                    : intl.get('password_recovery_send_code')}
                </MDTypography>
              </MDLoadingButton>
            </MDBox>

            <MDBox mt={2} mb={1}>
              <Link to="/">
                <MDTypography
                  component="span"
                  variant="body2"
                  style={{ color: 'white' }}>
                  {intl.get('password_recovery_back_to_login')}
                </MDTypography>
              </Link>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
    );
  }

  function renderNewPasswordForm() {
    return (
      <Card
        id="BasicLayoutChildrenCard"
        sx={{ backgroundColor: 'rgba(52, 52, 52, 0.8)' }}>
        <MDBox pt={4} pb={3} px={3} sx={{ width: '100%' }}>
          <MDBox mb={3}>
            <MDTypography style={{ color: 'white' }}>
              <MDTypography
                component="span"
                fontWeight="bold"
                style={{ color: 'white' }}>
                {fields.email}
              </MDTypography>{' '}
              {intl.get('password_recovery_code_sent')}
            </MDTypography>
          </MDBox>
          <MDBox
            component="form"
            role="form"
            onSubmit={handleResetPasswordClick}>
            <MDBox mb={2}>
              <MDInput
                required
                fullWidth
                name="code"
                value={fields.code}
                onChange={handleInput}
                sx={{ input: { background: '#FFFFFF', color: '#000' } }}
              />
            </MDBox>

            <MDBox mb={3}>
              <MDTypography style={{ color: 'white' }}>
                {intl.get('password_recovery_new_password')}
              </MDTypography>
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                required
                fullWidth
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={fields.password}
                onChange={handleInput}
                sx={{ input: { color: '#000', background: '#FFFFFF' } }}
                InputProps={{
                  sx: { background: '#FFFFFF' },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => {
                          setShowPassword(!showPassword);
                        }}
                        color="info">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </MDBox>

            <MDBox mb={3}>
              <MDTypography style={{ color: 'white' }}>
                {intl.get('password_recovery_confirm_password')}
              </MDTypography>
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                required
                fullWidth
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                value={fields.confirmPassword}
                onChange={handleInput}
                sx={{ input: { color: '#000', background: '#FFFFFF' } }}
                InputProps={{
                  sx: { background: '#FFFFFF' },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => {
                          setShowConfirmPassword(!showConfirmPassword);
                        }}
                        color="info">
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </MDBox>

            {state.invalidResetForm &&
              renderInvalidResetFormErrorMessage(state.invalidResetFormMessage)}
            {state.passwordResetFailed &&
              renderPasswordResetFailedErrorMessage(
                state.passwordResetFailedReason
              )}

            <MDBox mt={4} mb={1}>
              <MDLoadingButton
                loading={state.isSettingPassword}
                disabled={state.isSettingPassword}
                type="submit"
                variant="gradient"
                fullWidth
                sx={{
                  backgroundColor: '#194796',
                  '&:hover': {
                    backgroundColor: '#194796',
                  },
                  '&:disabled': {
                    backgroundColor: '#194796',
                  },
                }}>
                <MDTypography variant="h5" fontWeight="bold" color="white">
                  {state.isSettingPassword
                    ? intl.get('login_please_wait')
                    : intl.get('password_recovery_send_code')}
                </MDTypography>
              </MDLoadingButton>
            </MDBox>

            <MDBox mt={2} mb={1}>
              <MDTypography
                component="span"
                variant="body2"
                style={{ color: 'white', cursor: 'pointer' }}
                onClick={() =>
                  dispatch({ type: RECOVERY_STATES.GO_BACK_TO_PAGE1 })
                }>
                {intl.get('password_recovery_back_to_page1')}
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
    );
  }

  function renderSuccessMessage() {
    return (
      <Card
        id="BasicLayoutChildrenCard"
        sx={{ backgroundColor: pallete[loginMode].cardBackgroundColor }}>
        <MDBox pt={4} pb={3} px={3} sx={{ width: '100%' }}>
          <MDBox mb={3}>
            <MDTypography variant="h4" style={{ color: 'white' }}>
              {intl.get('password_recovery_password_reset')}
            </MDTypography>
          </MDBox>

          <MDBox mt={4} mb={1} sx={{ textAlign: 'center' }}>
            <Link to="/">
              <MDButton
                type="submit"
                variant="gradient"
                sx={{ backgroundColor: '#C81414' }}>
                <MDTypography variant="body1" fontWeight="bold" color="white">
                  {intl.get('password_recovery_back_to_login')}
                </MDTypography>
              </MDButton>
            </Link>
          </MDBox>
        </MDBox>
      </Card>
    );
  }

  return (
    <BasicLayout
      image={loginMode === 0 ? newBgImage : bgImage}
      loginMode={loginMode}>
      {!state.codeSent
        ? renderRequestCodeForm()
        : !state.passwordIsReset
        ? renderNewPasswordForm()
        : renderSuccessMessage()}
    </BasicLayout>
  );
}
